import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, eur, frDate } from "@/lib/api";
import ManualBookingForm from "@/pages/admin/ManualBookingForm";
import BookingEditForm from "@/pages/admin/BookingEditForm";
import BookingCard from "@/pages/admin/BookingCard";
import BookingDocuments from "@/pages/admin/BookingDocuments";

const BADGE = {
  confirmed: "bg-brass text-ink", approved: "bg-white/10 text-bone",
  requested: "bg-brass/20 text-brass", declined: "bg-white/5 text-muted-foreground",
  cancelled: "bg-white/5 text-muted-foreground",
};
const LABEL = { requested: "à valider", approved: "lien de paiement envoyé", confirmed: "confirmé", declined: "refusé", cancelled: "annulé" };
const METHOD_LABEL = { especes: "espèces", virement: "virement", cheque: "chèque", airbnb: "Airbnb", autre: "hors ligne" };
const DEPOSIT_LABEL = {
  link_sent: "caution : en attente du voyageur", link_expired: "caution : lien expiré",
  held: "caution autorisée (bloquée)", captured: "caution prélevée",
  released: "caution libérée", expired: "caution expirée",
};
const frDateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");
export const depositNote = (b) => {
  if (b.deposit_status === "held" && b.deposit_expires_early)
    return `⚠ empreinte valable jusqu'au ${frDateTime(b.deposit_expires_at)} — avant la fin des 48 h après départ`;
  if (b.deposit_status === "held" && b.deposit_expires_at)
    return `valable jusqu'au ${frDateTime(b.deposit_expires_at)}`;
  if (b.deposit_status === "expired")
    return "⚠ somme non garantie — aucun prélèvement possible";
  return null;
};

const payLabel = (b) => {
  if (b.payment_status === "manual") return `réglé en ${METHOD_LABEL[b.payment_method] || "direct"}`;
  if (b.payment_status === "paid" || b.status === "confirmed") return "encaissé";
  if (b.payment_status === "authorized") return "carte pré-autorisée";
  return "carte non enregistrée";
};

const BookingRow = ({ b, giteName, onDecide, onDeposit, onCancel, onOffline, onDelete, onEdit,
                      onDepositLink, onDepositRelease, onDocs }) => (
  <tr className="border-b border-white/10" data-testid={`booking-row-${b.id}`}>
    <td className="px-4 py-3">
      <p className="font-medium text-bone">{b.name}</p>
      <p className="text-xs text-muted-foreground">{b.email}</p>
      {b.phone && <p className="text-xs text-muted-foreground">{b.phone}</p>}
    </td>
    <td className="px-4 py-3 text-muted-foreground">{giteName}</td>
    <td className="px-4 py-3 text-muted-foreground">{frDate(b.start_date)} → {frDate(b.end_date)} <span className="text-xs">({b.nights} n.)</span></td>
    <td className="px-4 py-3">{b.guests}{b.adults != null ? ` / ${b.adults}+` : ""}</td>
    <td className="px-4 py-3 text-xs text-muted-foreground">{b.purpose || "—"}</td>
    <td className="px-4 py-3 font-medium">{eur(b.total)}</td>
    <td className="px-4 py-3">
      <span className={`px-2.5 py-1 text-[11px] font-semibold ${BADGE[b.status] || ""}`}>{LABEL[b.status] || b.status}</span>
      <span className="mt-1 block text-[10px] text-muted-foreground" data-testid={`pay-state-${b.id}`}>{payLabel(b)}</span>
      {b.deposit_charged > 0 && <span className="mt-1 block text-[10px] text-brass">caution {eur(b.deposit_charged)}</span>}
      {DEPOSIT_LABEL[b.deposit_status] && (
        <span className="mt-1 block text-[10px] text-brass" data-testid={`deposit-state-${b.id}`}>
          {DEPOSIT_LABEL[b.deposit_status]}
        </span>
      )}
      {depositNote(b) && (
        <span className={`mt-1 block text-[10px] ${b.deposit_expires_early || b.deposit_status === "expired" ? "text-destructive" : "text-muted-foreground"}`}
          data-testid={`deposit-note-${b.id}`}>{depositNote(b)}</span>
      )}
    </td>
    <td className="px-4 py-3 text-right">
      {["requested", "approved"].includes(b.status) && (
        <>
          <button onClick={() => onDecide(b.id, "approve")} className="mr-3 text-xs text-brass underline" data-testid={`approve-${b.id}`}>Valider & encaisser</button>
          <button onClick={() => onOffline(b.id)} className="mr-3 text-xs text-bone underline" data-testid={`offline-${b.id}`}>Confirmer (espèces)</button>
          <button onClick={() => onDecide(b.id, "decline")} className="mr-3 text-xs underline" data-testid={`decline-${b.id}`}>Refuser</button>
        </>
      )}
      {b.status === "confirmed" && b.deposit_status !== "held" && b.deposit_status !== "released" && (
        <button onClick={() => onDepositLink(b.id)} className="mr-3 text-xs text-brass underline" data-testid={`deposit-link-${b.id}`}>Lien caution</button>
      )}
      {b.deposit_status === "held" && (
        <>
          <button onClick={() => onDeposit(b.id)} className="mr-3 text-xs text-brass underline" data-testid={`deposit-${b.id}`}>Prélever</button>
          <button onClick={() => onDepositRelease(b.id)} className="mr-3 text-xs underline" data-testid={`deposit-release-${b.id}`}>Libérer</button>
        </>
      )}
      {b.status === "confirmed" && b.deposit_status !== "held" && b.deposit_status !== "released"
        && b.deposit_status !== "expired" && b.stripe_payment_method_id && (
        <button onClick={() => onDeposit(b.id)} className="mr-3 text-xs text-brass underline" data-testid={`deposit-fallback-${b.id}`}>Caution</button>
      )}
      {b.status !== "cancelled" && <button onClick={() => onCancel(b.id)} className="mr-3 text-xs text-destructive underline" data-testid={`cancel-${b.id}`}>Annuler</button>}
      <button onClick={() => onDocs(b)} className="mr-3 text-xs text-brass underline" data-testid={`docs-${b.id}`}>Documents</button>
      <button onClick={() => onEdit(b)} className="mr-3 text-xs text-bone underline" data-testid={`edit-${b.id}`}>Modifier</button>
      <button onClick={() => onDelete(b.id, b.name)} className="text-xs text-destructive underline" data-testid={`delete-${b.id}`}>Supprimer</button>
    </td>
  </tr>
);

export default function BookingsTab({ gites, onChanged }) {
  const [rows, setRows] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [editing, setEditing] = useState(null);
  const [docs, setDocs] = useState(null);
  const [deposit, setDeposit] = useState({ id: null, amount: "", reason: "" });
  const [cancel, setCancel] = useState({ id: null, reason: "", plan: null });
  const load = useCallback(() => api.get("/admin/bookings")
    .then((r) => setRows(r.data))
    .catch((e) => {
      setRows([]);
      toast.error(e.response?.status === 401
        ? "Session expirée — reconnectez-vous pour voir vos réservations"
        : `Chargement impossible : ${apiError(e.response?.data?.detail)}`);
    }), []);
  const refresh = useCallback(() => { load(); onChanged?.(); }, [load, onChanged]);
  useEffect(() => { load(); }, [load]);

  const openCancel = async (id) => {
    try {
      const { data } = await api.get(`/admin/bookings/${id}/refund-preview`);
      setCancel({ id, reason: "", plan: data });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doCancel = async () => {
    try {
      const { data } = await api.post(`/admin/bookings/${cancel.id}/cancel`, { reason: cancel.reason });
      toast.success(data.refunded > 0
        ? `Annulé — ${eur(data.refunded)} remboursés (${data.rate} % du séjour)`
        : `Annulé — aucun remboursement (${data.rate} %)`);
      setCancel({ id: null, reason: "", plan: null });
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmOffline = async (id) => {
    if (!window.confirm("Confirmer ce séjour comme réglé en espèces ? Toute empreinte bancaire sera libérée.")) return;
    try {
      await api.post(`/admin/bookings/${id}/confirm-offline`, { payment_method: "especes" });
      toast.success("Séjour confirmé — paiement en espèces, email de confirmation envoyé");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement la réservation de ${name} ? Cette action est irréversible.`)) return;
    try {
      const { data } = await api.delete(`/admin/bookings/${id}`);
      toast.success(data.released ? "Supprimée — empreinte bancaire libérée" : "Réservation supprimée");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sendDepositLink = async (id) => {
    if (!window.confirm("Envoyer au voyageur le lien d'empreinte bancaire pour la caution ?")) return;
    try {
      const { data } = await api.post(`/admin/bookings/${id}/deposit-link`, { origin_url: window.location.origin });
      toast.success(`Lien de caution de ${eur(data.amount)} envoyé par email`);
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const releaseDeposit = async (id) => {
    if (!window.confirm("Libérer la caution ? La somme bloquée est rendue au voyageur, plus aucun prélèvement ne sera possible.")) return;
    try {
      await api.post(`/admin/bookings/${id}/deposit-release`);
      toast.success("Caution libérée — le voyageur est prévenu par email");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const decide = async (id, action) => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/decide`, {
        action, reason: action === "decline" ? "Dates finalement indisponibles" : null,
        origin_url: window.location.origin,
      });
      toast.success(data.status === "confirmed"
        ? (data.already_paid ? "Validé — paiement déjà encaissé, aucun nouveau débit" : `Validé — ${eur(data.captured)} encaissés`)
        : data.status === "approved" ? "Validé — lien de paiement envoyé au voyageur"
          : "Refusé — empreinte bancaire libérée");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const charge = async () => {
    try {
      await api.post(`/admin/bookings/${deposit.id}/deposit`, {
        amount: Number(deposit.amount), reason: deposit.reason,
      });
      toast.success("Caution prélevée, emails envoyés");
      setDeposit({ id: null, amount: "", reason: "" });
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const name = (s) => gites.find((g) => g.slug === s)?.name || s;

  return (
    <div data-testid="bookings-tab">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <button onClick={() => setShowManual((v) => !v)} className="btn-primary !py-2 !px-4 !text-xs" data-testid="new-manual-btn">
          + Réservation hors ligne
        </button>
        <button onClick={refresh} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="refresh-bookings-btn">
          Rafraîchir
        </button>
        <p className="text-xs text-muted-foreground">
          Sur le site, les voyageurs paient uniquement par carte. Ce bouton sert aux séjours réglés
          en espèces, par virement ou par chèque.
        </p>
      </div>

      {showManual && (
        <ManualBookingForm gites={gites} onCreated={refresh} onClose={() => setShowManual(false)} />
      )}

      <div className="space-y-3 lg:hidden" data-testid="bookings-cards">
        {rows.map((b) => (
          <BookingCard key={b.id} b={b} giteName={name(b.gite_slug)}
            label={LABEL[b.status] || b.status} payLabel={payLabel(b)}
            depositLabel={DEPOSIT_LABEL[b.deposit_status]} depositNote={depositNote(b)}
            onDecide={decide} onDeposit={(id) => setDeposit({ id, amount: "", reason: "" })}
            onCancel={openCancel} onOffline={confirmOffline} onDelete={remove}
            onEdit={(row) => setEditing(row)} onDocs={(row) => setDocs(row)}
            onDepositLink={sendDepositLink} onDepositRelease={releaseDeposit} />
        ))}
        {!rows.length && <p className="border border-white/10 bg-panel p-10 text-center text-sm text-muted-foreground">Aucune réservation pour le moment.</p>}
      </div>

      <div className="hidden overflow-x-auto border border-white/10 bg-panel lg:block">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Client", "Gîte", "Séjour", "Pers.", "Motif", "Total", "Statut", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <BookingRow key={b.id} b={b} giteName={name(b.gite_slug)}
                onDecide={decide}
                onDeposit={(id) => setDeposit({ id, amount: "", reason: "" })}
                onCancel={openCancel} onOffline={confirmOffline} onDelete={remove}
                onEdit={(row) => setEditing(row)} onDocs={(row) => setDocs(row)}
                onDepositLink={sendDepositLink} onDepositRelease={releaseDeposit} />
            ))}
            {!rows.length && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Aucune réservation pour le moment.</td></tr>}
          </tbody>
        </table>
      </div>

      {docs && (
        <BookingDocuments booking={docs} onClose={() => setDocs(null)} />
      )}

      {editing && (
        <BookingEditForm booking={editing} onSaved={refresh} onClose={() => setEditing(null)} />
      )}

      {deposit.id && (() => {
        const dep = rows.find((r) => r.id === deposit.id) || {};
        const max = Math.max(0, (dep.deposit_amount || 350) - (dep.deposit_charged || 0));
        return (
        <div className="mt-5 border border-brass/40 bg-panel p-6" data-testid="deposit-panel">
          <h3 className="font-display text-xl text-bone">Prélèvement sur la caution ({eur(max)} disponibles)</h3>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <label className="text-sm">
              <span className="mb-1.5 block text-xs text-bone">Montant (€)</span>
              <input type="number" min="1" max={max} className="field !py-2 w-32" data-testid="deposit-amount"
                value={deposit.amount} onChange={(e) => setDeposit({ ...deposit, amount: e.target.value })} />
            </label>
            <label className="flex-1 text-sm">
              <span className="mb-1.5 block text-xs text-bone">Justification (envoyée au client) *</span>
              <input className="field !py-2" data-testid="deposit-reason" value={deposit.reason}
                onChange={(e) => setDeposit({ ...deposit, reason: e.target.value })} />
            </label>
            <button onClick={charge} className="btn-primary" data-testid="deposit-submit">Prélever</button>
            <button onClick={() => setDeposit({ id: null, amount: "", reason: "" })} className="btn-ghost">Annuler</button>
          </div>
        </div>
        );
      })()}

      {cancel.id && (
        <div className="mt-5 border border-destructive/40 bg-panel p-6" data-testid="cancel-panel">
          <h3 className="font-display text-xl text-bone">Annulation et remboursement automatique</h3>
          {cancel.plan && (
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground" data-testid="cancel-plan">
              Arrivée dans {cancel.plan.days_before_arrival} jour(s) → politique :
              <strong className="text-bone"> {cancel.plan.rate} % du séjour</strong> ({eur(cancel.plan.stay_refund)})
              + taxe de séjour ({eur(cancel.plan.tourist_tax_refund)}) =
              <strong className="text-brass"> {eur(cancel.plan.amount)}</strong> remboursés sur {eur(cancel.plan.paid)} payés.
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <label className="flex-1 text-sm">
              <span className="mb-1.5 block text-xs text-bone">Motif (envoyé au client)</span>
              <input className="field !py-2" data-testid="cancel-reason" value={cancel.reason}
                onChange={(e) => setCancel({ ...cancel, reason: e.target.value })} />
            </label>
            <button onClick={doCancel} className="btn-primary" data-testid="cancel-confirm">Annuler & rembourser</button>
            <button onClick={() => setCancel({ id: null, reason: "", plan: null })} className="btn-ghost">Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
